import { ref, reactive, computed } from 'vue';
import Auth from 'dashboard/api/auth';

const MAX_FILE_SIZE_MB = 40;
const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
];

function createClientId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `file-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function useCopilotAttachments({ accountId }) {
  const pendingFiles = ref([]);

  const hasFiles = computed(() => pendingFiles.value.length > 0);
  const isUploading = computed(() => pendingFiles.value.some(f => f.uploading));

  function getAuthHeaders() {
    if (!Auth.hasAuthCookie()) return {};
    const {
      'access-token': accessToken,
      'token-type': tokenType,
      client,
      expiry,
      uid,
    } = Auth.getAuthData();
    return {
      'access-token': accessToken,
      'token-type': tokenType,
      client,
      expiry,
      uid,
    };
  }

  async function uploadFile(entry) {
    const formData = new FormData();
    formData.append('attachment', entry.file);

    try {
      entry.uploading = true;
      entry.error = null;

      const response = await fetch(`/api/v1/accounts/${accountId}/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const data = await response.json();
      entry.fileUrl = data.file_url;
      entry.blobId = data.blob_id;
    } catch (err) {
      entry.error = err.message;
    } finally {
      entry.uploading = false;
    }
  }

  function addFiles(fileList) {
    const added = [];

    for (const file of fileList) {
      const clientId = createClientId();
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > MAX_FILE_SIZE_MB) {
        const entry = reactive({
          clientId,
          file,
          name: file.name,
          preview: null,
          uploading: false,
          fileUrl: null,
          blobId: null,
          error: `File too large (${sizeMB.toFixed(1)}MB, max ${MAX_FILE_SIZE_MB}MB)`,
          isImage: false,
          mimeType: file.type,
        });
        pendingFiles.value.push(entry);
        added.push(entry);
        continue;
      }

      if (!ALLOWED_TYPES.includes(file.type)) {
        const entry = reactive({
          clientId,
          file,
          name: file.name,
          preview: null,
          uploading: false,
          fileUrl: null,
          blobId: null,
          error: 'File type not supported',
          isImage: false,
          mimeType: file.type,
        });
        pendingFiles.value.push(entry);
        added.push(entry);
        continue;
      }

      const isImage = file.type.startsWith('image/');
      const preview = isImage ? URL.createObjectURL(file) : null;

      const entry = reactive({
        clientId,
        file,
        name: file.name,
        preview,
        uploading: false,
        fileUrl: null,
        blobId: null,
        error: null,
        isImage,
        mimeType: file.type,
      });

      pendingFiles.value.push(entry);
      added.push(entry);
      uploadFile(entry);
    }

    return added;
  }

  function removeFile(index) {
    const entry = pendingFiles.value[index];
    if (entry?.preview) {
      URL.revokeObjectURL(entry.preview);
    }
    pendingFiles.value.splice(index, 1);
  }

  function removeFileByClientId(clientId) {
    const index = pendingFiles.value.findIndex(f => f.clientId === clientId);
    if (index === -1) return;
    removeFile(index);
  }

  function clear() {
    pendingFiles.value.forEach(entry => {
      if (entry.preview) URL.revokeObjectURL(entry.preview);
    });
    pendingFiles.value = [];
  }

  function getUploadedFiles() {
    return pendingFiles.value
      .filter(f => f.fileUrl && !f.error)
      .map(f => ({
        url: f.fileUrl,
        mimeType: f.mimeType,
        isImage: f.isImage,
        name: f.name,
      }));
  }

  return {
    pendingFiles,
    hasFiles,
    isUploading,
    addFiles,
    removeFile,
    removeFileByClientId,
    clear,
    getUploadedFiles,
  };
}
